import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Consent {
  purpose: string
  opted_in: number
  opted_out: number
  rate: number
  regulation: string
}

interface DSARRequest {
  id: string
  type: string
  subject: string
  submitted: string
  due_date: string
  status: string
  regulation: string
}

interface Statistics {
  total_subjects: number
  consent_rate: number
  dsar_requests_mtd: number
  avg_response_days: number
  data_breaches_ytd: number
  compliance_score: number
}

export default function PrivacyManagementPage() {
  const [consents, setConsents] = useState<Consent[]>([])
  const [dsarRequests, setDsarRequests] = useState<DSARRequest[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [consentRes, dsarRes, statsRes] = await Promise.all([
        fetch('/api/privacymanagement/consent'),
        fetch('/api/privacymanagement/dsar'),
        fetch('/api/privacymanagement/statistics')
      ])
      if (consentRes.ok) setConsents(await consentRes.json())
      if (dsarRes.ok) setDsarRequests(await dsarRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching privacy management:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'New') return 'bg-blue-100 text-blue-800'
    if (status === 'In Progress') return 'bg-yellow-100 text-yellow-800'
    if (status === 'Pending Approval') return 'bg-orange-100 text-orange-800'
    if (status === 'Completed') return 'bg-green-100 text-green-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Privacy Management</h1>
          <p className="mt-2 text-gray-600">Phase 265 - GDPR/CCPA compliance and consent</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Subjects</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_subjects)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Consent Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.consent_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">DSAR Requests MTD</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.dsar_requests_mtd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Response</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_response_days}d</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Breaches YTD</p>
              <p className="text-2xl font-bold text-green-600">{statistics.data_breaches_ytd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Compliance</p>
              <p className="text-2xl font-bold text-green-600">{statistics.compliance_score}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Consent Overview</h2>
          </div>
          <div className="p-6 space-y-4">
            {consents.map((consent, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-900">{consent.purpose}</span>
                    <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{consent.regulation}</span>
                  </div>
                  <span className="text-lg font-bold text-green-600">{consent.rate}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-2">
                  <div className="h-2 bg-green-500 rounded-full" style={{width: `${consent.rate}%`}}></div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Opted In</p>
                    <p className="font-medium text-green-600">{formatNumber(consent.opted_in)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Opted Out</p>
                    <p className="font-medium text-red-600">{formatNumber(consent.opted_out)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">DSAR Requests</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Regulation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {dsarRequests.map((req) => (
                  <tr key={req.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{req.type}</td>
                    <td className="px-6 py-4 text-gray-500">{req.subject}</td>
                    <td className="px-6 py-4 text-gray-500">{req.submitted}</td>
                    <td className="px-6 py-4 text-orange-600">{req.due_date}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{req.regulation}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(req.status)}`}>{req.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
